import React from "react";
import { z } from "zod";
import {
    vote as postVote,
    getVote as getVoteApi,
    getPollInfo,
    PollInfo,
} from "../clients/poll";
import { useSessionContext } from "./session";
import { useSendCommand } from "../hooks/use-send-command";
import { useGameParamsContext } from "./gameParams";
import { getAvatarId } from "../utils/jwt";

const LOCALSTORAGE_KEY = "poll-votes";

const VoteCache = z.object({
    avatarId: z.number().nullable(),
    votes: z.record(z.union([z.array(z.string()), z.null()])),
});
type VoteCache = z.infer<typeof VoteCache>;

const LegacyVoteCache = z.record(z.union([z.array(z.string()), z.null()]));

type VoteSuccess = {
    status: "ok";
    pollId: string;
    optionId: string[];
};
type VoteFailure = {
    status: "fail";
};
type VoteResult = VoteSuccess | VoteFailure;

type VotingState = "idle" | "voting" | "fail";

type PollState = {
    activePollId: string | null;
    votingState: VotingState;
    voteCache: VoteCache;
    polls: Record<string, PollInfo>;
};

const defaultState: PollState = {
    activePollId: null,
    votingState: "idle",
    voteCache: {
        avatarId: null,
        votes: {},
    },
    polls: {},
};

type PollAction =
    | { type: "OPEN_POLL_MODAL"; activePollId: string }
    | { type: "CLOSE_POLL_MODAL" }
    | {
          type: "UPDATE_VOTE";
          pollId: string;
          optionId: string[] | null;
          avatarId: number | null;
      }
    | { type: "UPDATE_POLL_INFO"; pollId: string; pollInfo: PollInfo }
    | { type: "START_VOTING" }
    | { type: "FINISH_VOTING"; result: VoteResult; avatarId: number | null }
    | { type: "CLEAR_CACHE" };

const PollContext = React.createContext<{
    state: PollState;
    canVote: (pollId: string) => boolean;
    hasVoted: (pollId: string) => boolean;
    isClosed: (pollId: string) => boolean;
    actions: {
        closeModal: () => void;
        getVote: (pollId: string) => void;
        openModal: (pollId: string) => void;
        refreshPollInfo: (pollId: string) => void;
        voteFromModal: (optionId: string[]) => Promise<[void, void]>;
        vote: (pollId: string, optionId: string[]) => Promise<[void, void]>;
    };
}>({
    state: defaultState,
    canVote: () => false,
    hasVoted: () => false,
    isClosed: () => false,
    actions: {
        closeModal: () => null,
        getVote: () => null,
        openModal: () => null,
        refreshPollInfo: () => null,
        voteFromModal: () => Promise.resolve([undefined, undefined]),
        vote: () => Promise.resolve([undefined, undefined]),
    },
});

const reducer = (state: PollState, action: PollAction): PollState => {
    switch (action.type) {
        case "OPEN_POLL_MODAL": {
            return {
                ...state,
                activePollId: action.activePollId,
                votingState: "idle",
            };
        }
        case "CLOSE_POLL_MODAL": {
            return {
                ...state,
                activePollId: null,
            };
        }
        case "UPDATE_VOTE": {
            const votes = writeVoteToLocalStorage(
                action.pollId,
                action.optionId,
                action.avatarId,
            );
            return {
                ...state,
                voteCache: votes,
            };
        }
        case "UPDATE_POLL_INFO": {
            const polls = {
                ...state.polls,
                [action.pollId]: action.pollInfo,
            };
            return {
                ...state,
                polls,
            };
        }
        case "START_VOTING": {
            return {
                ...state,
                votingState: "voting",
            };
        }
        case "FINISH_VOTING": {
            if (action.result.status === "ok") {
                const votes = writeVoteToLocalStorage(
                    action.result.pollId,
                    action.result.optionId,
                    action.avatarId,
                );
                return {
                    ...state,
                    voteCache: votes,
                    votingState: "idle",
                };
            }
            return {
                ...state,
                votingState: "fail",
            };
        }
        case "CLEAR_CACHE": {
            clearVotesFromLocalStorage();
            return {
                ...state,
                voteCache: {
                    avatarId: null,
                    votes: {},
                },
            };
        }
        default:
            throw new Error("PollContext: Unhandled action type");
    }
};

const PollContextProvider = ({
    children,
    initialState,
}: {
    children: JSX.Element | JSX.Element[];
    initialState: PollState;
}): JSX.Element | null => {
    const {
        state: { sessionToken, avatarId: sessionAvatarId },
        actions: { refreshSession },
    } = useSessionContext();
    const { gameParams } = useGameParamsContext();
    const jwt = gameParams.jwt;

    const sendCommand = useSendCommand();

    const [state, dispatch] = React.useReducer(reducer, initialState);

    const onPopState = React.useCallback((event: PopStateEvent) => {
        if (event.state?.home) {
            closeModal();
        }
    }, []);

    React.useEffect(() => {
        window.addEventListener("popstate", onPopState);

        return () => {
            window.removeEventListener("popstate", onPopState);
        };
    }, []);

    React.useEffect(() => {
        if (
            sessionAvatarId &&
            state.voteCache.avatarId &&
            sessionAvatarId !== state.voteCache.avatarId
        ) {
            console.log(
                "POLL AVATAR ID MISMATCH",
                sessionAvatarId,
                state.voteCache.avatarId,
            );
            dispatch({ type: "CLEAR_CACHE" });
        }
    }, [sessionAvatarId, state.voteCache.avatarId]);

    const getVote = (pollId: string) => {
        if (!sessionToken) {
            return;
        }
        getVoteApi(pollId, sessionToken).then((optionId) => {
            dispatch({
                type: "UPDATE_VOTE",
                pollId,
                optionId,
                avatarId: sessionAvatarId,
            });
        });
    };

    const refreshPollInfo = (pollId: string) => {
        return getPollInfo(pollId)
            .then((pollInfo) => {
                dispatch({
                    type: "UPDATE_POLL_INFO",
                    pollId,
                    pollInfo,
                });
            })
            .catch(() => {
                refreshSession();
            });
    };

    const vote = (pollId: string, optionId: string[]) => {
        if (!sessionToken) {
            return Promise.resolve([undefined, undefined] as [void, void]);
        }

        dispatch({ type: "START_VOTING" });
        return Promise.all([
            postVote(pollId, optionId, sessionToken, jwt ?? "")
                .then((response) => {
                    if (response.type === "error") {
                        throw new Error(response.message);
                    }

                    const avatarId = getAvatarId(jwt ?? "") ?? null;

                    dispatch({
                        type: "FINISH_VOTING",
                        result: { status: "ok", pollId, optionId },
                        avatarId,
                    });
                })
                .catch(() => {
                    dispatch({
                        type: "FINISH_VOTING",
                        result: { status: "fail" },
                        avatarId: null,
                    });
                    refreshSession();
                    getVote(pollId);
                }),
            refreshPollInfo(pollId),
        ]);
    };

    const voteFromModal = (optionId: string[]) => {
        if (!sessionToken || !state.activePollId) {
            return Promise.resolve([undefined, undefined] as [void, void]);
        }
        return vote(state.activePollId, optionId);
    };

    const openModal = (pollId: string) => {
        sendCommand("SHOW_BACK_BUTTON", true);
        window.history.pushState({ home: false }, "");
        dispatch({ type: "OPEN_POLL_MODAL", activePollId: pollId });
    };

    const closeModal = () => {
        sendCommand("SHOW_BACK_BUTTON", false);
        dispatch({ type: "CLOSE_POLL_MODAL" });
    };

    const closeModalAction = () => {
        window.history.replaceState({ home: true }, "");
        closeModal();
    };

    const canVote = (pollId: string) => {
        return !hasVoted(pollId) && sessionToken !== null;
    };

    const hasVoted = (pollId: string) => {
        const hasVoted =
            pollId in state.voteCache.votes &&
            state.voteCache.votes[pollId] !== null;
        return hasVoted;
    };

    const isClosed = (pollId: string) => {
        const isClosed = state.polls[pollId]?.status === "closed";
        return isClosed;
    };

    return (
        <PollContext.Provider
            value={{
                state,
                canVote,
                isClosed,
                hasVoted,
                actions: {
                    closeModal: closeModalAction,
                    getVote,
                    openModal,
                    refreshPollInfo,
                    voteFromModal,
                    vote,
                },
            }}
        >
            {children}
        </PollContext.Provider>
    );
};

export const withPollContextProvider =
    (Component: typeof React.Component) => (props: Record<string, unknown>) => {
        return (
            <PollContextProvider
                initialState={{
                    ...defaultState,
                    voteCache: readVotesFromLocalStorage(),
                }}
            >
                <Component {...props} />
            </PollContextProvider>
        );
    };

export const usePollContext = () => React.useContext(PollContext);

export const clearVotesFromLocalStorage = () => {
    localStorage.removeItem(LOCALSTORAGE_KEY);
};

const readVotesFromLocalStorage = (): VoteCache => {
    try {
        const data = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY) ?? "");
        const votes = VoteCache.safeParse(data);

        if (votes.success) {
            return votes.data;
        }

        const legacyVotes = LegacyVoteCache.safeParse(data);
        if (legacyVotes.success) {
            return {
                avatarId: null,
                votes: legacyVotes.data,
            };
        }

        throw new Error("Invalid data");
    } catch (error) {
        clearVotesFromLocalStorage();
        return {
            avatarId: null,
            votes: {},
        };
    }
};

const writeVoteToLocalStorage = (
    pollId: string,
    optionId: string[] | null,
    avatarId: number | null,
) => {
    const currentVotes = readVotesFromLocalStorage();
    const votes = {
        votes: { ...currentVotes.votes, [pollId]: optionId },
        avatarId,
    };
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(votes));
    return votes;
};
